/**
 * The page-level shaping of the budget consolidation — the filter AST and the fiscal-period window that
 * narrow **which lines count** (PlanRollup-View-Shaping §7, §8), plus their URL encoding (§12).
 *
 * Page-level, not per tab, and that is load-bearing: filter and period range apply identically to every
 * tab, which is what keeps the tabs comparable with each other (§9.1). The grouping chain is the opposite
 * — it belongs to the one configurable tab, because it re-partitions rather than narrows.
 *
 * Kept apart from the components so the encoding can be tested without rendering a page, and so the four
 * tabs share one definition of "is anything shaping this view" rather than each deciding for itself.
 */

import type { ConditionDto } from '@/types/core/Rules';

export interface RollupShaping {
  /** A serialized `ConditionDto`, or undefined for no filter. Stored as the string the API takes. */
  filterJson?: string;
  /** Inclusive lower bound of the period window. */
  fromFiscalPeriodId?: string;
  /** Inclusive upper bound. */
  toFiscalPeriodId?: string;
}

export const EMPTY_SHAPING: RollupShaping = {};

/**
 * Whether anything is narrowing the view — the condition §11's whole disclosure hangs on. A filter with
 * no rules in it asks for nothing, so an editor left open on an empty group does not count as shaping;
 * treating it as such would put "(filtered)" on a view identical to the unfiltered one.
 */
export function isShapingActive(shaping: RollupShaping): boolean {
  return countConditions(shaping.filterJson) > 0
    || !!shaping.fromFiscalPeriodId
    || !!shaping.toFiscalPeriodId;
}

/** Parse a stored filter, tolerating anything unparseable as "no filter" — never throwing at a reader. */
export function parseFilter(filterJson: string | undefined): ConditionDto | null {
  if (!filterJson) return null;
  try {
    return JSON.parse(filterJson) as ConditionDto;
  } catch {
    return null;
  }
}

/** How many leaf conditions the filter carries — the count on the Filter button. */
export function countConditions(filterJson: string | undefined): number {
  return countRules(parseFilter(filterJson));
}

function countRules(node: ConditionDto | null | undefined): number {
  if (!node) return 0;
  const anyNode = node as any;
  if (Array.isArray(anyNode.children)) {
    return anyNode.children.reduce((sum: number, child: ConditionDto) => sum + countRules(child), 0);
  }
  return anyNode.field ? 1 : 0;
}

/**
 * The leaf conditions, flattened — what the toolbar renders as individually-removable chips so the
 * filter is legible without opening the editor (§9).
 */
export function listConditions(filterJson: string | undefined): ConditionRule[] {
  const out: ConditionRule[] = [];
  collect(parseFilter(filterJson), out);
  return out;
}

export interface ConditionRule {
  nodeId?: string;
  field: string;
  operator: string;
  value?: unknown;
}

function collect(node: ConditionDto | null | undefined, out: ConditionRule[]): void {
  if (!node) return;
  const anyNode = node as any;
  if (Array.isArray(anyNode.children)) {
    anyNode.children.forEach((child: ConditionDto) => collect(child, out));
    return;
  }
  if (anyNode.field) {
    out.push({ nodeId: anyNode.nodeId, field: anyNode.field, operator: anyNode.operator, value: anyNode.value });
  }
}

/**
 * Removes one leaf, identified by its `nodeId`, and returns the filter to store — undefined once the
 * last condition goes, so an emptied filter is indistinguishable from never having had one (and the URL
 * loses the parameter rather than carrying an empty AST).
 *
 * Groups left childless are dropped with it: a stored AST full of empty groups still counts as a filter
 * to anything that only checks for a value, and would keep the view labelled as filtered forever.
 */
export function removeCondition(filterJson: string | undefined, nodeId: string | undefined): string | undefined {
  const ast = parseFilter(filterJson);
  if (!ast || !nodeId) return filterJson;
  const pruned = prune(ast, nodeId);
  if (!pruned || countRules(pruned) === 0) return undefined;
  return JSON.stringify(pruned);
}

function prune(node: ConditionDto, nodeId: string): ConditionDto | null {
  const anyNode = node as any;
  if (Array.isArray(anyNode.children)) {
    const children = anyNode.children
      .map((child: ConditionDto) => prune(child, nodeId))
      .filter((child: ConditionDto | null): child is ConditionDto => child !== null);
    return children.length === 0 ? null : { ...anyNode, children };
  }
  return anyNode.nodeId === nodeId ? null : node;
}

/**
 * Reads the shaping out of a query string. Shareable and bookmarkable rather than persisted — saved view
 * definitions wait for a concrete ask (§12).
 */
export function parseShaping(query: URLSearchParams): RollupShaping {
  const shaping: RollupShaping = {};
  const filter = query.get('filter');
  // An unparseable filter is dropped rather than carried: the API would only widen the read and warn,
  // and the chips would have nothing to render, so the reader would see "filtered" with no conditions.
  if (filter && parseFilter(filter)) shaping.filterJson = filter;
  const from = query.get('from');
  if (from) shaping.fromFiscalPeriodId = from;
  const to = query.get('to');
  if (to) shaping.toFiscalPeriodId = to;
  return shaping;
}

/**
 * Writes the shaping into a query string, omitting everything unset so the common link stays short —
 * §12.1's budget is spent on the selection rather than on restating defaults.
 */
export function writeShaping(query: URLSearchParams, shaping: RollupShaping): void {
  if (countConditions(shaping.filterJson) > 0 && shaping.filterJson) query.set('filter', shaping.filterJson);
  if (shaping.fromFiscalPeriodId) query.set('from', shaping.fromFiscalPeriodId);
  if (shaping.toFiscalPeriodId) query.set('to', shaping.toFiscalPeriodId);
}

/**
 * The shaping fields of the request body, in the API's own names. Every rollup read takes the same three,
 * which is what makes "the tabs narrow identically" a property of one object rather than of four call
 * sites agreeing.
 */
export function shapingRequestFields(shaping: RollupShaping): Record<string, string | undefined> {
  return {
    filterJson: shaping.filterJson,
    fromFiscalPeriodId: shaping.fromFiscalPeriodId,
    toFiscalPeriodId: shaping.toFiscalPeriodId,
  };
}

/** A stable React Query key part — the shaping is part of what a cached read is a read *of*. */
export function shapingQueryKey(shaping: RollupShaping): string {
  return [shaping.filterJson ?? '', shaping.fromFiscalPeriodId ?? '', shaping.toFiscalPeriodId ?? ''].join('|');
}
